import type { SolverConfiguration } from '@lib/transfers/store/preferences-slice';

export type SolverConfigurationKey = keyof SolverConfiguration;

export type ConfigurationSliderDefinition = {
  key: SolverConfigurationKey;
  title: string;
  hint: string;
  lowAnchor: string;
  highAnchor: string;
  min: number;
  max: number;
  step: number;
  formatValue: (value: number) => string;
};

export type ConfigurationSliderGroup = {
  heading: string;
  sliders: ConfigurationSliderDefinition[];
};

const formatGameweeks = (value: number) =>
  `${value} GW${value === 1 ? '' : 's'}`;

// Trims float artefacts from stepped values (e.g. 0.8500000000000001).
const formatNumber = (value: number) => `${Number(value.toFixed(2))}`;

export const SLIDER_GROUPS: ConfigurationSliderGroup[] = [
  {
    heading: 'Horizon',
    sliders: [
      {
        key: 'planningRange',
        title: 'Planning range',
        hint: 'How many gameweeks ahead the AI plans lineups, captain and transfers. Longer takes longer to run.',
        lowAnchor: '1 GW',
        highAnchor: '6 GWs',
        min: 1,
        max: 6,
        step: 1,
        formatValue: formatGameweeks
      },
      {
        key: 'predictionHorizon',
        title: 'Prediction horizon',
        hint: 'How far ahead it scores the consequences, even though moves are only planned inside the planning range.',
        lowAnchor: '2 GWs',
        highAnchor: '8 GWs',
        min: 2,
        max: 8,
        step: 1,
        formatValue: formatGameweeks
      },
      {
        key: 'futureWeighting',
        title: 'Future weighting',
        hint: 'How much later gameweeks count against this one.',
        lowAnchor: 'Short term',
        highAnchor: 'Long term',
        min: 0.05,
        max: 1,
        step: 0.05,
        formatValue: formatNumber
      }
    ]
  },
  {
    heading: 'Squad limits',
    sliders: [
      {
        key: 'maxPlayersPerTeam',
        title: 'Max players per team',
        hint: 'Caps how dependent the squad can be on a single club.',
        lowAnchor: '1',
        highAnchor: '3',
        min: 1,
        max: 3,
        step: 1,
        formatValue: formatNumber
      },
      {
        key: 'maxDefensivePlayersPerTeam',
        title: 'Max defenders + GK per team',
        hint: 'Diversifies your defensive picks across clubs.',
        lowAnchor: '1',
        highAnchor: '3',
        min: 1,
        max: 3,
        step: 1,
        formatValue: formatNumber
      }
    ]
  },
  {
    heading: 'Strategy',
    sliders: [
      {
        key: 'transferValue',
        title: 'Transfer value',
        hint: 'What a free transfer is worth to hold onto.',
        lowAnchor: 'Use them now',
        highAnchor: 'Bank them',
        min: 0,
        max: 4,
        step: 0.25,
        formatValue: formatNumber
      },
      {
        key: 'subImportance',
        title: 'Sub importance',
        hint: 'How much the bench counts in planning.',
        lowAnchor: 'Ignore bench',
        highAnchor: 'Bench matters',
        min: 0,
        max: 1,
        step: 0.01,
        formatValue: formatNumber
      }
    ]
  }
];

export const CONFIGURATION_KEYS = SLIDER_GROUPS.flatMap((group) =>
  group.sliders.map((slider) => slider.key)
);

const sliderDefinitionsByKey = new Map(
  SLIDER_GROUPS.flatMap((group) => group.sliders).map((slider) => [
    slider.key,
    slider
  ])
);

/**
 * Keeps planningRange <= predictionHorizon, mirroring the backend's
 * cross-field rule, so a value chosen here is never one the API will
 * reject. Moving one slider past the other pulls the other along just far
 * enough to stay valid.
 */
export const applyConfigurationCommit = (
  configuration: SolverConfiguration,
  key: SolverConfigurationKey,
  value: number
): SolverConfiguration => {
  const next = { ...configuration, [key]: value };
  if (key === 'planningRange' && value > next.predictionHorizon) {
    next.predictionHorizon = value;
  } else if (key === 'predictionHorizon' && value < next.planningRange) {
    next.planningRange = value;
  }
  return next;
};

// The finest slider step is 0.01, so two decimal places are enough to trim
// the float artefacts step snapping can introduce (e.g. 0.8500000000000001).
const STEP_PRECISION = 2;

const snapToSlider = (
  value: number,
  slider: ConfigurationSliderDefinition
): number => {
  const stepsFromMin = Math.round((value - slider.min) / slider.step);
  const snapped = Number(
    (slider.min + stepsFromMin * slider.step).toFixed(STEP_PRECISION)
  );
  return Math.min(Math.max(snapped, slider.min), slider.max);
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Repairs a persisted custom configuration against the current slider
 * bounds and the planningRange <= predictionHorizon invariant. A member's
 * saved value can predate a bounds tightening (e.g. predictionHorizon's
 * max moving from 12 to 8) or the cross-field rule itself; sending it to
 * the API unchanged would repeat the same VALIDATION_ERROR on every
 * request until they reopen and re-apply the profile.
 *
 * The input is whatever localStorage handed back, so nothing about its
 * shape is trusted: each field must be a finite number, which is then
 * snapped to the slider's step and clamped to its bounds. A field that is
 * missing or not a number cannot be repaired without inventing a value the
 * member never chose, so the whole configuration is dropped (null) and the
 * caller falls back to the backend's default profile.
 */
export const repairPersistedConfiguration = (
  configuration: unknown
): SolverConfiguration | null => {
  if (typeof configuration !== 'object' || configuration === null) {
    return null;
  }
  const candidate = configuration as Record<string, unknown>;
  const perFieldClamped: Partial<SolverConfiguration> = {};

  for (const key of CONFIGURATION_KEYS) {
    const slider = sliderDefinitionsByKey.get(key);
    const value = candidate[key];
    if (!slider || !isFiniteNumber(value)) {
      return null;
    }
    perFieldClamped[key] = snapToSlider(value, slider);
  }

  const clamped = perFieldClamped as SolverConfiguration;
  return applyConfigurationCommit(
    clamped,
    'planningRange',
    clamped.planningRange
  );
};
